import {
  Box,
  Container,
  Link,
  NeoColor,
  Stack,
  ThemeProvider,
  neoTheme,
} from "@noom/wax-component-library";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { CareChevronLeftIcon } from "../CareChevronLeftIcon";
import { useHideDocumentScrollbar } from "../hooks/useHideDocumentScrollbar";

export const DetailShell = ({ children }: { children: ReactNode }) => {
  useHideDocumentScrollbar();

  return (
    <ThemeProvider theme={neoTheme}>
      <Container bg={NeoColor.greyscale.white} minH="100vh">
        <Stack
          width="100%"
          // Matches CareTabPage's max width so the page width stays the
          // same when navigating from /care to /care/treatment/<code>.
          maxWidth="480px"
          margin="0 auto"
          spacing={0}
          alignItems="stretch"
        >
          <DetailPageHeader />
          {children}
        </Stack>
      </Container>
    </ThemeProvider>
  );
};

const DetailPageHeader = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { t, ready = true } = useTranslation("care", { useSuspense: false });

  // Preserve the header's height while translations load without exposing a
  // raw key as the back button's accessible label.
  if (!ready) {
    return <Box width="100%" height="56px" />;
  }

  return (
    <Box
      width="100%"
      height="56px"
      display="flex"
      alignItems="center"
    >
      <Link
        as="button"
        type="button"
        // Replay the current query string so presentation params the Care tab
        // was opened with (e.g. `hideTitle`) are not dropped on the way back.
        onClick={() => navigate({ pathname: "/care", search })}
        aria-label={t("detail.backToCare")}
        color={NeoColor.greyscale.black}
        minW="44px"
        h="44px"
        display="inline-flex"
        alignItems="center"
        justifyContent="flex-start"
        p={0}
      >
        <CareChevronLeftIcon />
      </Link>
    </Box>
  );
};
